import mcpService, { type Mcp } from '../services/mcpService';

export type ResolverMcpRedondeoResult =
  | { status: 'ok'; mcp: Mcp }
  | { status: 'multiple'; opciones: Mcp[] }
  | { status: 'none' };

/**
 * Resuelve el MCP de redondeo del grupo/caja (solo con esRedondeo activo).
 * - 1 → ok
 * - >1 → multiple (abrir diálogo de selección)
 * - 0 → none (hay que crear/configurar uno con el flag)
 */
export async function resolverMcpRedondeo(grupo: number): Promise<ResolverMcpRedondeoResult> {
  if (!(Number(grupo) > 0)) return { status: 'none' };
  const opciones = await mcpService.listarMcpsRedondeo(grupo);
  if (opciones.length === 0) return { status: 'none' };
  if (opciones.length === 1) return { status: 'ok', mcp: opciones[0] };
  return { status: 'multiple', opciones };
}

/** Máximo absoluto configurado en el MCP. */
export function limiteRedondeoAbs(
  mcp: Pick<Mcp, 'limiteRedondeo'> | null | undefined,
): number {
  return Math.abs(Number(mcp?.limiteRedondeo) || 0);
}

/** Mayor tope entre varios MCP de redondeo. */
export function limiteMaxEntreMcps(
  mcps: Pick<Mcp, 'limiteRedondeo'>[] | null | undefined,
): number {
  if (!mcps?.length) return 0;
  return mcps.reduce((max, m) => Math.max(max, limiteRedondeoAbs(m)), 0);
}

/** true si |diferencia| está dentro del máximo dado (MCP o número). */
export function diferenciaCabeEnRedondeo(
  diferencia: number,
  mcpOrLimite: Pick<Mcp, 'limiteRedondeo'> | null | undefined | number,
): boolean {
  const lim =
    typeof mcpOrLimite === 'number' ? Math.abs(mcpOrLimite || 0) : limiteRedondeoAbs(mcpOrLimite);
  if (!(lim > 0)) return Math.abs(diferencia) < 0.005;
  return Math.abs(diferencia) <= lim + 1e-9;
}

/** Etiqueta UI de tipo de pago a partir del código MCP (E, T, C, …). */
export function tipoPagoUiFromMcpTipo(tipo: string | null | undefined): string {
  switch ((tipo || '').trim().toUpperCase()) {
    case 'E':
      return 'Efectivo';
    case 'C':
      return 'Cheque';
    case 'T':
      return 'Tarjeta';
    case 'TB':
      return 'Transferencia';
    case 'P':
      return 'Cheque propio';
    case 'R':
      return 'Retención';
    case 'O':
    default:
      return 'Otros';
  }
}

/**
 * Importe de la línea de redondeo para cuadrar medios vs aplicado.
 * - Falta (medios < aplicado, diferencia &lt; 0) → +|diff|
 * - Sobra (medios &gt; aplicado, diferencia &gt; 0) → −|diff|
 */
export function importeLineaRedondeo(diferencia: number): number | null {
  const abs = Math.abs(Number(diferencia) || 0);
  if (!(abs > 0)) return null;
  return diferencia < 0 ? abs : -abs;
}

/**
 * true si hay diferencia y cabe en el tope disponible.
 * `limiteDisponible`: mayor máximo entre los MCP de redondeo del grupo (0 si no hay).
 * No asume “sí” cuando aún no se conoce el tope: hay que pasar 0 o el valor prefetch.
 */
export function puedeAplicarRedondeo(
  diferencia: number,
  diferenciaSinSaldo: boolean,
  limiteDisponible: number,
): boolean {
  if (diferenciaSinSaldo) return false;
  const importe = importeLineaRedondeo(diferencia);
  if (importe == null) return false;
  return diferenciaCabeEnRedondeo(Math.abs(importe), limiteDisponible);
}

/** MCP cuyo límite alcanza |importeAbs|. */
export function filtrarMcpsQueCubren(
  mcps: Mcp[],
  importeAbs: number,
): Mcp[] {
  return mcps.filter((m) => diferenciaCabeEnRedondeo(importeAbs, m));
}
